import React, { FC, useMemo } from 'react';
import { Button, Card, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';

import { usePermissions } from '~/hooks';
import { getTransactions } from '~/api/transactions';
import { Transaction } from '~/types';
import { AmountField, Space, StatusField } from '~/components';
import { transactions } from '~/routes';
import { ArrowRedoIcon } from '~/img';
import theme from '~/theme';
import GridLayout from '~/layout/GridLayout';
import TransactionCard from '../transactions/TransactionCard';

const Transactions: FC<any> = (props) => {
  const { className } = props;
  const { data, isLoading } = useQuery('homeScreenTransactions', () =>
    getTransactions({ limit: 8 })().then((res) => res.data)
  );
  const [t] = useTranslation('transactions');
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  const columns: ColumnsType<Transaction> = useMemo<ColumnsType<Transaction>>(
    () => [
      {
        title: t('posted'),
        dataIndex: 'postedAmount',
        align: 'right',
        render: (_, record: Transaction) => {
          return <AmountField amount={record.postedAmount} />;
        },
      },
      {
        title: t('instructed'),
        dataIndex: 'amount',
        align: 'right',
        render: (_, record: Transaction) => {
          return <AmountField amount={record.amount} />;
        },
      },
      {
        title: t('description'),
        dataIndex: 'description',
      },
      {
        title: t('status'),
        dataIndex: 'status',
        render: (_, record: Transaction) => {
          return <StatusField data={record.status} addLabel={false} />;
        },
      },
      {
        title: t('date'),
        dataIndex: 'createdAt',
        width: 235,
        render: (_, record: Transaction) => {
          return <div>{moment(record.createdAt).format('LL LT')}</div>;
        },
      },
    ],
    [t]
  );

  const hasAccess = usePermissions('business.transactions.list');
  if (!hasAccess) return null;
  if (isMobile) {
    return (
      <MobileContainer className={className}>
        <GridLayout className="Transactions-TopContent" justify="space-between" align="middle">
          <Typography.Title className="Transactions-Title" level={4}>
            {t('lastTransactions')}
          </Typography.Title>
          <Link to={`/${transactions}`}>
            <Button type="primary" icon={<ArrowRedoIcon />}>
              {t('showMore')}
            </Button>
          </Link>
        </GridLayout>
        <Space direction="vertical" size={16}>
          {data?.transactions?.map((item: any) => {
            return <TransactionCard key={item.id} record={item} />;
          })}
        </Space>
      </MobileContainer>
    );
  }
  return (
    <div className={className}>
      <Card
        title={t('lastTransactions')}
        extra={
          <Link to={`/${transactions}`}>
            <Button icon={<ArrowRedoIcon />} type="primary">
              {t('showMore')}
            </Button>
          </Link>
        }
      >
        <Table<Transaction>
          columns={columns as ColumnsType<Transaction>}
          dataSource={data?.transactions}
          loading={isLoading}
          rowKey={(record) => record.id}
          pagination={{ hideOnSinglePage: true }}
        />
      </Card>
    </div>
  );
};

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  .Transactions-TopContent {
    margin-bottom: 16px;
    .Transactions-Title {
      margin-bottom: 0;
    }
  }
`;

export default Transactions;
